import { ErrorResponse, UserManager, WebStorageStateStore, Log } from "oidc-client-ts";
import type { User } from "oidc-client-ts";
import { config, issuer } from "./config";

let mgr: UserManager | null = null;

/**
 * OIDC-oppsettet. Authorization Code Flow med PKCE - oidc-client-ts slår på
 * PKCE automatisk for `response_type: "code"`, og S256 er det eneste realmen
 * godtar (`pkce.code.challenge.method: S256`).
 *
 * Biblioteket henter discovery-dokumentet fra issueren og validerer ID-tokenet
 * mot realmens JWKS: signatur, `iss`, `aud`, `exp` og `nonce`. Det er der
 * tokenvalideringen hører hjemme - ikke i vår egen kode.
 *
 * Opprettes først ved første kall, ikke når modulen lastes. Konstruktøren
 * starter stille fornyelse med en gang (`automaticSilentRenew`): ligger det en
 * bruker i sessionStorage, settes en timer som kaller IdP-en. /status skal
 * virke når id.actiwise.no er nede, og må derfor aldri komme hit.
 */
export function userManager(): UserManager {
  mgr ??= new UserManager({
    authority: issuer,
    client_id: config.clientId,

    // Må stemme eksakt med `redirectUris` på klienten i realmen.
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: window.location.origin,

    response_type: "code",

    // `roles`, `basic`, `acr` og `web-origins` er default client scopes på
    // klienten og kommer med uten at vi ber om dem.
    scope: "openid profile email",

    // Stille fornyelse med roterende refresh tokens, ikke skjult iframe.
    // Iframe-varianten er avhengig av tredjeparts-cookies, som nettleserne er
    // i ferd med å fjerne. Realmen har `revokeRefreshToken: true` og
    // `refreshTokenMaxReuse: 0`, altså rotasjon med gjenbruksdeteksjon.
    //
    // Merk: dette forutsetter at brukeren HAR et refresh token. Har hen ikke
    // det, feiler signinSilent, og vi ber om ny innlogging i stedet for å
    // falle tilbake på iframe (som ville krevd en ekstra redirect-URI i
    // realmen).
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,

    // sessionStorage, ikke localStorage: tokens overlever ikke at fanen lukkes,
    // og deles ikke mellom faner. Litt mindre bekvemt, men vesentlig mindre
    // eksponert enn localStorage om det skulle finnes et XSS-hull.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  return mgr;
}

if (import.meta.env.DEV) {
  Log.setLogger(console);
  Log.setLevel(Log.INFO);
}

export async function gjeldendeBruker(): Promise<User | null> {
  return userManager().getUser();
}

export async function loggInn(): Promise<void> {
  await userManager().signinRedirect();
}

/**
 * Tokens ligger i sessionStorage, så en ny fane starter uten bruker - selv om
 * SSO-sesjonen i Keycloak (cookie på id.actiwise.no) lever. Med
 * `prompt=none` svarer Keycloak uten å vise noe: en kode hvis sesjonen
 * finnes, `login_required` hvis ikke. Da slipper brukeren et «Logg inn»-
 * klikk som uansett ikke ville bedt om passord.
 *
 * Forsøket gjøres én gang per fane. Flagget ligger i sessionStorage av samme
 * grunn som tokenene, og hindrer en løkke om svaret skulle bli en feil.
 *
 * `replace`, ikke `assign`: ellers ville tilbake-knappen sendt brukeren til
 * en authorize-URL hvis state allerede er brukt opp.
 */
const STILLE_FORSOK = "actiwise.stille-innlogging-forsokt";

export function stilleInnloggingForsokt(): boolean {
  return window.sessionStorage.getItem(STILLE_FORSOK) !== null;
}

function merkStilleInnloggingForsokt(): void {
  window.sessionStorage.setItem(STILLE_FORSOK, "1");
}

export function nullstillStilleInnlogging(): void {
  window.sessionStorage.removeItem(STILLE_FORSOK);
}

export async function stilleInnlogging(): Promise<void> {
  merkStilleInnloggingForsokt();
  await userManager().signinRedirect({
    prompt: "none",
    redirectMethod: "replace",
  });
}

/**
 * Feilkodene Keycloak gir på `prompt=none` når brukeren må gjøre noe selv
 * (OIDC Core 3.1.2.6). Det er ikke en feil for oss - bare et nei.
 */
const KREVER_SAMHANDLING = new Set([
  "login_required",
  "interaction_required",
  "consent_required",
  "account_selection_required",
]);

export function kreverSamhandling(feil: unknown): boolean {
  return (
    feil instanceof ErrorResponse &&
    feil.error !== null &&
    KREVER_SAMHANDLING.has(feil.error)
  );
}

/**
 * Et utløpt access token betyr ikke at sesjonen er over - refresh tokenet
 * kan fortsatt være gyldig (f.eks. etter at maskinen har sovet). Bruker
 * kun refresh tokenet: uten et slikt ville signinSilent falt tilbake på
 * iframe, som vi bevisst ikke bruker.
 */
export async function fornyMedRefreshToken(bruker: User): Promise<User | null> {
  if (!bruker.refresh_token) return null;
  return userManager().signinSilent();
}

/**
 * RP-initiated logout. Sender `id_token_hint` til Keycloaks end_session-
 * endepunkt, slik at SSO-sesjonen faktisk avsluttes - ikke bare den lokale
 * kopien av tokenet.
 *
 * NB: dette logger brukeren ut av IdP-en, men IKKE ut av Reports, Companion
 * eller Cabinet. De appene har ingen backchannel-logout-URL registrert ennå.
 * Se README ("Kjente begrensninger").
 */
export async function loggUt(): Promise<void> {
  // Den som nettopp logget ut skal møte «Logg inn», ikke et automatisk hopp
  // til Keycloak.
  merkStilleInnloggingForsokt();
  await userManager().signoutRedirect();
}
